import type {ReactNode} from 'react';

export type TextSize = 'xsmall' | 'small' | 'medium' | 'large';
export type TextWeight = 'regular' | 'medium' | 'semi-bold' | 'bold';

type TextProps = {
    children: ReactNode;
    size?: TextSize;
    fontWeight?: TextWeight;
    textTransform?: 'uppercase';
    className?: string;
};

const classes = (
    base: string,
    size: TextSize,
    weight: TextWeight,
    transform: 'uppercase' | undefined,
    extra: string | undefined,
): string =>
    [
        'sd-text',
        base,
        `sd-text-${size}`,
        `sd-weight-${weight}`,
        transform === undefined ? '' : `sd-text-${transform}`,
        extra ?? '',
    ]
        .filter(Boolean)
        .join(' ');

/**
 * Body text, in the app's own type scale: 11 / 13 / 14 / 16px, four weights. Inline spans, so a
 * run of text sits in whatever flow its parent has; layout is the parent's job.
 */
export const Text = ({
    children,
    size = 'medium',
    fontWeight = 'medium',
    textTransform,
    className,
}: TextProps) => (
    <span className={classes('sd-text-primary', size, fontWeight, textTransform, className)}>
        {children}
    </span>
);

/** The quieter voice: secondary colour, regular weight. */
export const SecondaryText = ({
    children,
    size = 'medium',
    fontWeight = 'regular',
    textTransform,
    className,
}: TextProps) => (
    <span className={classes('sd-text-secondary', size, fontWeight, textTransform, className)}>
        {children}
    </span>
);

/** A screen's title where the display face would be too much — the access screen. */
export const PageTitle = ({children}: {children: ReactNode}) => (
    <h1 className="sd-page-title">{children}</h1>
);

/**
 * The text components under one name, so a call site reads `Typography.Text` as it always has.
 * These replaced Malachite's: the design is the app's own now, and so is its type.
 */
export const Typography = {Text, SecondaryText, PageTitle};
